// src/App.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAuth }      from './context/AuthContext';
import { useUser }      from './hooks/useUser';
import { useHouses }    from './hooks/useHouses';
import { useInventory } from './hooks/useInventory';
import ItemCard         from './components/ItemCard';
import AddEditModal     from './components/AddEditModal';
import SettingsModal    from './components/SettingsModal';
import HouseSelector    from './components/HouseSelector';
import RecipesModal     from './components/RecipesModal';
import Toast, { toast } from './components/Toast';
import { daysLeft, getStatus, buildDemoItems } from './lib/utils';
import { updateDoc, doc, setDoc } from "firebase/firestore";
import { getRedirectResult } from "firebase/auth";
import { auth, db } from "./lib/firebase";
import Login from "./pages/Login";
import StockSettings from './pages/StockSettings';

const API_URL = "https://wltm.fr/api";

const FILTERS = [
  { key:'all',          label:'Tout' },
  { key:'sec',          label:'🧂 Sec' },
  { key:'frigo',        label:'❄️ Frigo' },
  { key:'congelateur',  label:'🧊 Congél.' },
  { key:'boissons',     label:'🥤 boissons' },
  { key:'entretien',    label:'🧴 entretien' },
  { key:'soon',         label:'⏳ Bientôt' },
  { key:'expired',      label:'❌ Périmés' },
];

export default function App() {

    useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        console.log("Redirect result:", result);

        if (!result?.user) return;

        await setDoc(
          doc(db, "users", result.user.uid),
          {
            email: result.user.email || "",
            name: result.user.displayName || "",
            alertDays: 7,
            createdAt: new Date().toISOString(),
            lastHouseId: "",
            lastAlert: null,
          },
          { merge: true }
        );
      })
      .catch((error) => {
        console.error("Redirect error:", error);
        alert(error.message);
      });
  }, []);

  const [stockOpen, setStockOpen] = useState(false);
  const { user, error: authError, logout } = useAuth();
  const { userData, loading: uLoading, save: saveUser } = useUser(user?.uid);
  const { houses, loading: hLoading, addHouse, renameHouse, removeHouse, updateHouseDoc } = useHouses(user?.uid);

  // ── Maison active ────────────────────────────────────────────
  const [activeHouseId, setActiveHouseId] = useState(null);

  const activeHouse = houses.find(h => h.id === activeHouseId) || null;

  useEffect(() => {
  if (!houses.length) return;

  if (userData?.lastHouseId) {
    setActiveHouseId(userData.lastHouseId);
  } else {
    setActiveHouseId(houses[0].id);
  }
}, [houses, userData]);

  const { inventory, loading: invLoading, add, update, remove, changeQty } = useInventory(activeHouseId);

  // ── Modals ───────────────────────────────────────────────────
  const [addOpen,    setAddOpen]    = useState(false);
  const [editItem,   setEditItem]   = useState(null);
  const [settOpen,   setSettOpen]   = useState(false);
  const [houseOpen,  setHouseOpen]  = useState(false);
  const [recOpen,    setRecOpen]    = useState(false);

  // ── Filtres ──────────────────────────────────────────────────
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  // ── Démo : pré-remplir si l'inventaire est vide ──────────────
  // ── Démo : charger une seule fois par maison ──────────────
  const demoRef = useRef(false);

  useEffect(() => {
    if (!activeHouseId) return;
    if (invLoading) return;
    if (!activeHouse) return;
  
    // 🔧 initialise si champ absent
    if (activeHouse.hasDemo === undefined) {
      updateHouseDoc(activeHouseId, { hasDemo: false });
      return;
    }
  
    // 🔒 déjà fait
    if (activeHouse.hasDemo) return;
  
    if (demoRef.current) return;
    demoRef.current = true;
  
    const loadDemo = async () => {
      for (const item of buildDemoItems()) {
        await add(item);
      }
  
      await updateHouseDoc(activeHouseId, { hasDemo: true });
    };
  
    loadDemo();
  }, [activeHouseId, invLoading, activeHouse, add, updateHouseDoc]);

  // Reset le flag quand on change de maison
  useEffect(() => { demoRef.current = false; }, [activeHouseId]);

  // ── Alerte automatique MailerSend au démarrage ────────────────
  const alertSentRef = useRef(false);
  useEffect(() => {
    if (!user?.uid || !activeHouseId || !userData.email || invLoading) return;
    if (alertSentRef.current) return;

    const thr   = parseInt(userData.alertDays || 7, 10);
    const items = inventory.filter(i => { const d = daysLeft(i.expiry); return d !== null && d <= thr; });
    if (!items.length) return;

    // Vérifier si déjà envoyé aujourd'hui
    if (userData.lastAlert) {
      const last = new Date(userData.lastAlert); last.setHours(0,0,0,0);
      const now  = new Date();                   now.setHours(0,0,0,0);
      if (last >= now) return;
    }

    alertSentRef.current = true;

    // Envoi automatique sans confirmation
    const timer = setTimeout(async () => {
      try {
        toast(`📧 Email envoyé à ${userData.email}`);///A REVOIR LE userData////
        const result = await fetch(`${API_URL}/send-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: userData.email,
            name: userData.name,
            houseName: activeHouse?.name,
            items: inventory.filter(i => {
              const d = daysLeft(i.expiry);
              return d !== null && d <= thr;
            }),
          }),
        }).then(r => r.json());
        if (result.data?.skipped) {
          toast('✅ Aucun produit urgent à signaler');
        } else {
          toast(`📧 Alerte envoyée — ${items.length} produit(s)`);
          await updateDoc(doc(db, "users", user.uid), {
            lastAlert: new Date().toISOString()
          });
        }
      } catch (err) {
        console.error('Auto-alert error:', err);
        // Pas de toast d'erreur pour l'envoi auto, on ne veut pas déranger l'utilisateur
      }
    }, 2000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line
  }, [user?.uid, activeHouseId, userData.email, invLoading, inventory.length]);

  // ── Stats ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const thr     = parseInt(userData.alertDays || 7, 10);
    const expired = inventory.filter(i => getStatus(i.expiry, thr) === 'expired').length;
    const soon    = inventory.filter(i => getStatus(i.expiry, thr) === 'soon').length;
    return { total: inventory.length, expired, soon, ok: inventory.length - expired - soon };
  }, [inventory, userData.alertDays]);

  // ── Liste filtrée ────────────────────────────────────────────
  const filtered = useMemo(() => {
    const thr = parseInt(userData.alertDays || 7, 10);
    let items = inventory.filter(i => {
      if (!search) return true;
      const q = search.toLowerCase();
      return i.name.toLowerCase().includes(q) || (i.note || '').toLowerCase().includes(q);
    });
    if (filter === 'sec')          items = items.filter(i => i.cat === 'sec');
    else if (filter === 'frigo')   items = items.filter(i => i.cat === 'frigo');
    else if (filter === 'congelateur') items = items.filter(i => i.cat === 'congelateur');
    else if (filter === 'boissons')    items = items.filter(i => i.cat === 'boissons');
    else if (filter === 'entretien')    items = items.filter(i => i.cat === 'entretien');
    else if (filter === 'soon')    items = items.filter(i => { const d = daysLeft(i.expiry); return d !== null && d >= 0 && d <= thr; });
    else if (filter === 'expired') items = items.filter(i => { const d = daysLeft(i.expiry); return d !== null && d < 0; });
    return [...items].sort((a, b) => (daysLeft(a.expiry) ?? 9999) - (daysLeft(b.expiry) ?? 9999));
  }, [inventory, filter, search, userData.alertDays]);

  useEffect(() => {
  if (!user?.uid || !activeHouseId) return;

  setDoc(
    doc(db, "users", user.uid),
    {
      lastHouseId: activeHouseId,
    },
    { merge: true }
  );
}, [activeHouseId, user?.uid]);

  // ── Handlers CRUD ────────────────────────────────────────────
  const handleSave = useCallback(async data => {
    if (editItem) { await update(editItem.id, data); toast('✅ Modifié'); }
    else          { await add(data);                 toast('✅ Ajouté'); }
    setEditItem(null);
  }, [editItem, add, update]);

  const handleQty = useCallback(async (id, delta) => {
    const newQty = await changeQty(id, delta);
    if (newQty === null) {
      const item = inventory.find(i => i.id === id);
      if (window.confirm(`Supprimer "${item?.name}" (quantité = 0) ?`)) {
        await remove(id); toast('🗑️ Supprimé');
      } else {
        await update(id, { qty: 1 });
      }
    }
  }, [changeQty, remove, update, inventory]);

  const handleDelete = useCallback(async item => {
    if (window.confirm(`Supprimer "${item.name}" ?`)) {
      await remove(item.id); toast('🗑️ Supprimé');
    }
  }, [remove]);

  const handleAddHouse = useCallback(async name => {
    const id = await addHouse(name);
    if (id) setActiveHouseId(id);
    toast(`🏠 "${name}" créée !`);
  }, [addHouse]);


  const handleSendStockAlert = useCallback(async () => {
  if (!activeHouseId) {
    toast('⚠️ Sélectionnez une maison');
    return;
  }

  if (!userData.email) {
    toast('⚠️ Ajoutez votre email dans ⚙️');
    setSettOpen(true);
    return;
  }

  toast('📦 Vérification du stock…');

  try {
    const itemsToSend = inventory.filter(i =>
      typeof i.stockMin === "number" &&
      typeof i.qty === "number" &&
      i.qty < i.stockMin
    );

    if (itemsToSend.length === 0) {
      toast('✅ Aucun produit sous le stock minimum');
      return;
    }

    const response = await fetch(`${API_URL}/send-stock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: userData.email,
        name: userData.name,
        houseName: activeHouse?.name,
        items: itemsToSend,
        type: "stock" // optionnel mais utile
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Erreur serveur");
    }

    toast(`📦 Alerte stock envoyée — ${itemsToSend.length} produit(s)`);

    await updateDoc(doc(db, "users", user.uid), {
      lastStockAlert: new Date().toISOString()
    });

  } catch (err) {
    toast('⚠️ ' + (err.message || 'Erreur envoi email'));
  }
}, [
  activeHouseId,
  userData.email,
  userData.name,
  user?.uid,
  inventory,
  activeHouse
]);

  const handleSendEmailNow = useCallback(async () => {
    if (!activeHouseId) { toast('⚠️ Sélectionnez une maison'); return; }
    if (!userData.email) { toast('⚠️ Ajoutez votre email dans ⚙️'); setSettOpen(true); return; }
    toast('📧 Envoi en cours…');
    try {
      const thr = parseInt(userData.alertDays || 7, 10);

const itemsToSend = inventory.filter(i => {
  const d = daysLeft(i.expiry);
  return typeof d === "number" && d <= thr;
});

console.log("ITEMS:", itemsToSend);

if (itemsToSend.length === 0) {
  toast('✅ Aucun produit à signaler');
  return;
}

const response = await fetch(`${API_URL}/send-email`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    email: userData.email,
    name: userData.name,
    houseName: activeHouse?.name,
    items: itemsToSend,
  }),
});

const result = await response.json();

console.log("RESULT:", result);

if (!response.ok) {
  throw new Error(result.error || "Erreur serveur");
}

toast(`📧 Email envoyé à ${userData.email}`);

await updateDoc(doc(db, "users", user.uid), {
  lastAlert: new Date().toISOString()
});
    } catch (err) {
      toast('⚠️ ' + (err.message || 'Erreur envoi email'));
    }
  }, [
  activeHouseId,
  userData.email,
  userData.name,
  user?.uid,
  inventory,
  activeHouse
]);
  if (authError) return <div>Erreur auth</div>;
  if (!user) return <Login />;

  const loading = hLoading || invLoading;

  return (
    <>
    {/* ── HEADER ── */}
<div className="hdr">

  {/* Ligne 1 : logo + maison */}
  <div className="hdr-main">
    <div className="logo">
      Ma <span>Réserve</span>
    </div>

    <button
      className="hdr-house"
      onClick={() => setHouseOpen(true)}
    >
      🏠 {activeHouse?.name || 'Choisir une maison'} ▾
    </button>
  </div>

  {/* Ligne 2 : boutons */}
  <div className="hdr-btns">
    <button className="hbtn" onClick={handleSendStockAlert} title="Vérifier stock">📉</button>
    <button className="hbtn" onClick={() => setStockOpen(true)} title="Stock">📦</button>
    <button className="hbtn" onClick={() => { setEditItem(null); setAddOpen(true); }} title="Ajouter">➕</button>
    <button className="hbtn" onClick={() => setSettOpen(true)} title="Paramètres">⚙️</button>
    <button className="hbtn" onClick={logout} title="Déconnexion">🚪</button>
  </div>

</div>


      {/* ── STATS ── */}
      <div className="stats">
        <div className="spill"><div className="dot dok" /><strong>{stats.total}</strong> produits</div>
        <div className="spill"><div className="dot dok" /><strong>{stats.ok}</strong> OK</div>
        <div className="spill"><div className="dot dsoon" /><strong>{stats.soon}</strong> bientôt</div>
        <div className="spill"><div className="dot dexp" /><strong>{stats.expired}</strong> périmés</div>
      </div>

      {/* ── ALERT BANNER ── */}
      {(stats.soon > 0 || stats.expired > 0) && (
        <div className="abanner" onClick={handleSendEmailNow}>
          ⚠️ <span>
            {[stats.expired && `${stats.expired} périmé(s)`, stats.soon && `${stats.soon} expirent bientôt`]
              .filter(Boolean).join(' · ')}
          </span>
          &nbsp;—&nbsp;<strong>Envoyer alerte email</strong>
        </div>
      )}

      {/* ── FILTERS ── */}
      <div className="filters">
        {FILTERS.map(f => (
          <button key={f.key} className={`fbtn${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      {/* ── SEARCH ── */}
      <div className="swrap">
        <span className="sicon">🔍</span>
        <input className="sinput" placeholder="Rechercher…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* ── LIST ── */}
      <div className="listwrap">
        {!activeHouseId ? (
          <div className="empty">
            <div className="ei">🏠</div>
            <p>Aucune maison sélectionnée.<br />
              <button className="bsubmit" style={{ marginTop:14, width:'auto', padding:'10px 24px' }}
                onClick={() => setHouseOpen(true)}>Créer ma première maison</button>
            </p>
          </div>
        ) : loading ? (
          <div className="empty"><div className="ei">⏳</div><p>Chargement…</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty"><div className="ei">🛒</div><p>Aucun produit.<br />Appuyez sur <strong>＋</strong> pour en ajouter un.</p></div>
        ) : (
          filtered.map(item => (
            <ItemCard key={item.id} item={item} alertDays={userData.alertDays}
              onEdit={it => { setEditItem(it); setAddOpen(true); }}
              onDelete={handleDelete} onQty={handleQty} />
          ))
        )}
      </div>

      {/* ── FABs ── */}
      <div className="fab-wrap">
        <button className="fab fab-recipe" onClick={() => setRecOpen(true)} title="Recettes">🍳</button>
        <button className="fab fab-add" onClick={() => { setEditItem(null); setAddOpen(true); }} title="Ajouter">＋</button>
      </div>

      {/* ── MODALS ── */}
      <AddEditModal open={addOpen}
        onClose={() => { setAddOpen(false); setEditItem(null); }}
        onSave={handleSave} editItem={editItem} />

      <HouseSelector open={houseOpen} onClose={() => setHouseOpen(false)}
        houses={houses} activeHouseId={activeHouseId}
        onSelect={setActiveHouseId} onAdd={handleAddHouse}
        onRename={renameHouse} onDelete={removeHouse}
        user={user} />

      <SettingsModal open={settOpen} onClose={() => setSettOpen(false)}
        userData={userData} onSave={saveUser} activeHouseId={activeHouseId} />

      <RecipesModal open={recOpen} onClose={() => setRecOpen(false)}
        inventory={inventory} alertDays={userData.alertDays} />

      {stockOpen && (
    <StockSettings
      inventory={inventory}
      houseId={activeHouseId}
      onClose={() => setStockOpen(false)}
    />
)}

      <Toast />
    </>
  );
}
